import {
  AddItemParams,
  CustomItemMode,
} from '../../itemDetails/types/CustomItem';
import {Box, HBox} from '../../../src/components/core/view';
import {H3Text, H5Text, H6Text} from '../../components/core/text';
import {HomeScreens, ShopperScreens} from '../../home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useAddItem, useAddItemReducer} from '../hooks/AddItem';
import {useColorThemeType, useFontTheme} from '../../appStyles/hooks/theme';

import {AddItemContext} from '../context/AddItem';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {BackHeaderLeft} from '@buncha/common/components/BackHeaderLeft';
import {BottomButton} from '../../components/composites/Button';
import Header from '../../components/composites/Header/Header';
import If from '../../components/conditional/If';
import {ItemDetailsScreen} from '../../itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import QRCode from '../../../src/components/core/icon/QRCode';
import SearchSection from '../component/SearchSection';
import {SpinnerComponent} from '../../components/core/loader';
import {Touchable} from '../../components/core/touchable';
import {isWeb} from '@buncha/utils/common';
import {navigation} from '../../config/navigation';
import {styles} from '../styles/AddItemPage';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

const AddItemPage = () => {
  const theme = useColorThemeType('main');
  const muliTheme = useFontTheme('muli');

  const [state, dispatch] = useAddItemReducer();

  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {title, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.AddItemPage) ?? {};

  const routeParams: any = useRoute().params;
  const runId = Number(routeParams?.runId);
  const orderId = Number(routeParams?.orderId);
  const storeId = Number(routeParams?.storeId);
  const returnPage = routeParams?.returnPage;
  const storeIndex = routeParams?.storeIndex || '';

  const isMeijerStore = useIsMeijerStore(storeId);

  const isWebMode = isWeb();

  const {selectedQuantity, selectedItem} = state;

  const {addItemLoading, addItem, errorMessage} = useAddItem();

  const [showErrorModal, setSetshowErrorModal] = useState('');

  const handleOnPress = useCallback(() => {
    if (!orderId || !runId) return;
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.CustomItem,
      params: {
        orderId,
        runId,
        mode: CustomItemMode.addItem,
        returnPage,
      },
    });
  }, [orderId, returnPage, runId]);

  useEffect(() => {
    if (errorMessage) setSetshowErrorModal(errorMessage);
  }, [errorMessage]);

  const handleOnClose = () => {
    setSetshowErrorModal('');
  };

  const navigateToBatchPage = useCallback(() => {
    if (returnPage === ShopperScreens.OrderPackingPage)
      return navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.OrderPackingPage,
        params: {runId},
      });
    navigation.navigate(HomeScreens.Shopper, {
      screen: ShopperScreens.BatchDetailsPage,
      params: {runId},
    });
  }, [returnPage, runId]);

  const navigateToScanner = useCallback(
    (orderItemId: number) => {
      if (isWebMode) return navigateToBatchPage();
      return navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.ItemScanner,
        params: {
          orderItemId: orderItemId,
          storeIndex,
          runId,
          quantity: selectedQuantity,
          returnPage,
          source: 'Add Item Screen',
        },
      });
    },
    [
      isWebMode,
      navigateToBatchPage,
      returnPage,
      runId,
      selectedQuantity,
      storeIndex,
    ],
  );

  const handleAddItem = useCallback(async () => {
    if (!orderId || !selectedItem || addItemLoading) return;
    if (isMeijerStore) {
      navigation.navigate(ShopperScreens.ScannedAddItemPage, {
        storeIndex: storeIndex,
        runId: runId,
        orderId: orderId,
        storeId: storeId,
        quantity: selectedQuantity,
        plu: selectedItem?.upc,
        returnPage,
      });
      return;
    }
    const params: AddItemParams = {
      runId,
      orderId: orderId,
      name: selectedItem.name,
      price: selectedItem.price,
      priceWithTax: selectedItem.price,
      quantity: selectedQuantity,
      isCustomItem: false,
      itemImageUrl: selectedItem.itemImageUrl,
      catalogItemId: selectedItem.id,
      tags: selectedItem.tags?.length ? selectedItem.tags[0] : '',
      position: 0,
      priceSensitive: Boolean(selectedItem.flags?.priceSensitive) ?? false,
      weightSensitive: Boolean(selectedItem?.flags?.weightSensitive) ?? false,
      itemUnit: selectedItem?.itemQuantity,
    };

    await addItem(params, navigateToScanner);
  }, [
    addItem,
    addItemLoading,
    isMeijerStore,
    navigateToScanner,
    orderId,
    returnPage,
    runId,
    selectedItem,
    selectedQuantity,
    storeId,
    storeIndex,
  ]);

  const getTitle = useMemo(() => {
    if (isWebMode) return button?.pickItem;
    return button?.foundItem;
  }, [button?.foundItem, button?.pickItem, isWebMode]);

  return (
    <Header
      style={styles.parentBox}
      headerColor={theme?.background?.blue?.[300]}
      showDefaultLeftHeader={false}
      headerRightComponent={
        <HBox style={[styles.header, {backgroundColor: theme?.text.blue[300]}]}>
          <BackHeaderLeft
            containerStyle={{backgroundColor: theme?.text.blue[300]}}
            iconColor={theme?.text.white[100]}
          />
          <Box style={styles.subHeader}>
            <H3Text
              style={[
                styles.headerText,
                {
                  color: theme?.text.white[100],
                  fontFamily: muliTheme[700].normal,
                },
              ]}>
              Add Item
            </H3Text>
          </Box>
        </HBox>
      }>
      <AddItemContext.Provider value={provider}>
        <SearchSection selectedItem={selectedItem} />
        <If condition={!selectedItem && !isMeijerStore}>
          <Touchable style={styles.bottomBox} onPress={handleOnPress}>
            <H6Text
              style={[styles.underlinedText, {color: theme?.text.gray[100]}]}>
              {title?.addCustomItem}
            </H6Text>
          </Touchable>
        </If>
        <If condition={selectedItem}>
          <Box style={styles.bottomBox}>
            <BottomButton
              leftComponent={
                !isWebMode && (
                  <QRCode viewBox="-5 -2 22 17" style={styles.iconBox} />
                )
              }
              title={getTitle}
              onPress={handleAddItem}
              isLoading={addItemLoading}
              disabled={addItemLoading}
              loadingIndicator={<SpinnerComponent />}
            />
          </Box>
        </If>
        <AppModal
          isOpen={Boolean(showErrorModal)}
          onClose={handleOnClose}
          topCTAHandelr={handleOnClose}
          title={button?.info}
          hideBottomCta
          modalBody={
            <HBox style={styles.modalBody}>
              <H5Text>{showErrorModal}</H5Text>
            </HBox>
          }
          ctaTopTitle={button?.ok}
          topCTAColor={theme?.background.green[100]}
          storeId={storeId}
        />
      </AddItemContext.Provider>
    </Header>
  );
};

export default React.memo(AddItemPage);
